import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "zen-button inline-flex items-center justify-center whitespace-nowrap rounded-md border text-sm font-semibold leading-none disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default: "zen-button-default",
        destructive: "zen-button-destructive",
        outline: "zen-button-outline",
        secondary: "zen-button-secondary",
        ghost: "zen-button-ghost",
        link: "zen-button-link",
      },
      size: {
        default: "min-h-[42px] px-[18px] py-0",
        sm: "min-h-8 rounded-[9px] px-3 py-0 text-xs",
        lg: "min-h-[52px] rounded-[14px] px-7 py-0 text-[1.05rem]",
        icon: "min-h-[42px] w-[42px] p-0",
        // A real 32px square. `size="icon"` plus an `h-8 w-8` override does NOT
        // give you one: `min-h-[42px]` survives the merge (twMerge has no
        // min-height/height conflict rule), so the button stays 42px tall and
        // sits unevenly beside anything actually 32px. 43 call sites in this
        // codebase pair `size="icon"` with an `h-*` override and every one of
        // them is silently taller than it looks.
        "icon-sm": "min-h-8 h-8 w-8 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  asyncState?: "idle" | "loading" | "success" | "error"
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, asyncState = "idle", ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const {
      "aria-busy": ariaBusy,
      "data-state": providedDataState,
      ...buttonProps
    } = props as ButtonProps & {
      "aria-busy"?: React.AriaAttributes["aria-busy"]
      "data-state"?: string
    }
    const dataState = asyncState === "idle" ? providedDataState : asyncState
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        data-state={dataState}
        aria-busy={asyncState === "loading" ? true : ariaBusy}
        ref={ref}
        {...buttonProps}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
